import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RefreshCw, Shield, Star, Minus, Plus, ChevronLeft } from 'lucide-react';
import Header from './components/Header13';
import Footer from './components/Footer13';

const ProductDetail13 = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [wishlisted, setWishlisted] = useState(false);

    const product = {
        id: id,
        name: 'Chanderi Silk Handloom Saree',
        price: 4999,
        originalPrice: 5999,
        rating: 4.8,
        reviews: 64,
        category: 'Handloom',
        description: 'Handwoven by master artisans of Chanderi, this silk saree features intricate zari borders and traditional butis. Each piece takes 3-5 days to weave on a traditional loom, making it a true collector\'s piece.',
        craftStory: 'This saree is woven in the historic town of Chanderi, Madhya Pradesh, where weaving traditions date back to the 2nd century BC. The translucent fabric with golden zari work is a hallmark of Chanderi craftsmanship.',
        features: [
            'Pure Chanderi silk with zari border',
            'Handwoven on traditional loom',
            'Natural dyes used throughout',
            'Comes with matching blouse piece',
            'Certificate of authenticity included',
        ],
        images: [
            'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        sizes: ['S', 'M', 'L', 'Free Size'],
        inStock: true,
    };

    const relatedProducts = [
        { id: 2, name: 'Jaipur Block Print Kurta', category: 'Block Print', price: 2499, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Pochampally Ikat Dupatta', category: 'Ikat', price: 1899, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Kalamkari Saree', category: 'Kalamkari', price: 6999, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Banarasi Silk Saree', category: 'Silk', price: 9999, image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Mulish:wght@300;400;600&display=swap');
            `}</style>

            <div style={{ fontFamily: "'Mulish', sans-serif" }} className="min-h-screen bg-[#FBF8F4]">
                <Header />

                <main style={{ paddingTop: '72px' }}>
                    {/* Breadcrumb */}
                    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
                        <button onClick={() => navigate(-1)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#78716C' }}>
                            <ChevronLeft size={16} /> Back to Shop
                        </button>
                    </div>

                    {/* Product Section */}
                    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px 60px' }}>
                        <div className="lg:grid lg:grid-cols-2 lg:gap-16">
                            {/* Images */}
                            <div>
                                <div style={{ aspectRatio: '4/5', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px', backgroundColor: '#F7F3ED' }}>
                                    <img src={product.images[selectedImage]} alt={product.name}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                                    {product.images.map((img, i) => (
                                        <button key={i} onClick={() => setSelectedImage(i)}
                                            style={{ width: '72px', height: '90px', borderRadius: '6px', overflow: 'hidden', border: selectedImage === i ? '2px solid #C2410C' : '2px solid #E7E0D6', cursor: 'pointer', flexShrink: 0, padding: 0, background: 'none' }}>
                                            <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Details */}
                            <div style={{ paddingTop: '16px' }} className="lg:pt-0">
                                <p style={{ fontSize: '11px', letterSpacing: '2px', color: '#C2410C', textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px' }}>
                                    {product.category}
                                </p>
                                <h1 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '28px', fontWeight: 700, color: '#1C1917', lineHeight: 1.3, marginBottom: '12px' }}
                                    className="lg:text-4xl">
                                    {product.name}
                                </h1>

                                {/* Rating */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', gap: '2px' }}>
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} size={14} style={{ fill: i < Math.floor(product.rating) ? '#C2410C' : '#E7E0D6', color: i < Math.floor(product.rating) ? '#C2410C' : '#E7E0D6' }} />
                                        ))}
                                    </div>
                                    <span style={{ fontSize: '13px', color: '#78716C' }}>{product.rating} ({product.reviews} reviews)</span>
                                </div>

                                {/* Price */}
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '24px' }}>
                                    <span style={{ fontSize: '28px', fontWeight: 600, color: '#1C1917' }}>{`\u20B9${product.price.toLocaleString()}`}</span>
                                    {product.originalPrice && (
                                        <span style={{ fontSize: '18px', color: '#78716C', textDecoration: 'line-through' }}>{`\u20B9${product.originalPrice.toLocaleString()}`}</span>
                                    )}
                                </div>

                                <p style={{ fontSize: '15px', color: '#78716C', lineHeight: 1.7, marginBottom: '28px' }}>{product.description}</p>

                                {/* Size Selection */}
                                <div style={{ marginBottom: '24px' }}>
                                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#1C1917', marginBottom: '10px', letterSpacing: '0.5px' }}>Size</p>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {product.sizes.map(size => (
                                            <button key={size} onClick={() => setSelectedSize(size)}
                                                style={{
                                                    padding: '10px 20px', borderRadius: '4px', cursor: 'pointer',
                                                    fontSize: '13px', fontWeight: 600, transition: 'all 0.3s',
                                                    backgroundColor: selectedSize === size ? '#C2410C' : 'transparent',
                                                    color: selectedSize === size ? '#FFFFFF' : '#1C1917',
                                                    border: selectedSize === size ? '2px solid #C2410C' : '2px solid #E7E0D6',
                                                }}>
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Quantity */}
                                <div style={{ marginBottom: '28px' }}>
                                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#1C1917', marginBottom: '10px', letterSpacing: '0.5px' }}>Quantity</p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0', border: '2px solid #E7E0D6', borderRadius: '4px', width: 'fit-content' }}>
                                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                            style={{ width: '44px', height: '44px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Minus size={16} color="#1C1917" />
                                        </button>
                                        <span style={{ width: '48px', textAlign: 'center', fontSize: '15px', fontWeight: 600, color: '#1C1917' }}>{quantity}</span>
                                        <button onClick={() => setQuantity(quantity + 1)}
                                            style={{ width: '44px', height: '44px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Plus size={16} color="#1C1917" />
                                        </button>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '32px' }}>
                                    <button style={{
                                        flex: 1, padding: '16px', backgroundColor: '#C2410C', color: '#FFFFFF',
                                        border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 600,
                                        letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        minHeight: '52px', transition: 'all 0.3s',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#9A3412'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C2410C'; }}>
                                        <ShoppingCart size={18} /> Add to Cart
                                    </button>
                                    <button onClick={() => setWishlisted(!wishlisted)}
                                        style={{
                                            width: '52px', height: '52px', border: '2px solid #E7E0D6', borderRadius: '4px',
                                            backgroundColor: 'transparent', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                        <Heart size={20} style={{ color: wishlisted ? '#C2410C' : '#78716C', fill: wishlisted ? '#C2410C' : 'none' }} />
                                    </button>
                                </div>

                                {/* Craft Story */}
                                <div style={{ backgroundColor: '#F7F3ED', borderRadius: '8px', padding: '20px', marginBottom: '24px', border: '1px solid #E7E0D6' }}>
                                    <h4 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '15px', fontWeight: 700, color: '#C2410C', marginBottom: '8px' }}>Craft Story</h4>
                                    <p style={{ fontSize: '13px', color: '#78716C', lineHeight: 1.7 }}>{product.craftStory}</p>
                                </div>

                                {/* Features */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '15px', fontWeight: 700, color: '#1C1917', marginBottom: '12px' }}>Details</h4>
                                    <ul style={{ listStyle: 'none', padding: 0 }}>
                                        {product.features.map((f, i) => (
                                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#78716C', marginBottom: '8px' }}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#C2410C', flexShrink: 0 }} />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Trust Badges */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', paddingTop: '24px', borderTop: '1px solid #E7E0D6' }}>
                                    {[
                                        { icon: Truck, label: 'Free Shipping', sub: 'Above \u20B9999' },
                                        { icon: RefreshCw, label: 'Easy Returns', sub: '7-day policy' },
                                        { icon: Shield, label: 'Authentic', sub: 'Certified craft' },
                                    ].map((item, i) => (
                                        <div key={i} style={{ textAlign: 'center' }}>
                                            <item.icon size={20} style={{ color: '#C2410C', margin: '0 auto 6px' }} />
                                            <p style={{ fontSize: '12px', fontWeight: 600, color: '#1C1917', marginBottom: '2px' }}>{item.label}</p>
                                            <p style={{ fontSize: '11px', color: '#78716C' }}>{item.sub}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Related Products */}
                    <div style={{ backgroundColor: '#F7F3ED', padding: '60px 20px' }}>
                        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                            <h2 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '24px', fontWeight: 700, color: '#1C1917', textAlign: 'center', marginBottom: '32px' }}
                                className="lg:text-3xl">
                                You May Also Like
                            </h2>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                                {relatedProducts.map(p => (
                                    <div key={p.id} onClick={() => navigate(`/template13/product/${p.id}`)}
                                        style={{ backgroundColor: '#FBF8F4', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E7E0D6', cursor: 'pointer', transition: 'all 0.3s' }}
                                        className="hover:shadow-lg hover:-translate-y-1">
                                        <div style={{ aspectRatio: '3/4', overflow: 'hidden' }}>
                                            <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div style={{ padding: '14px' }}>
                                            <p style={{ fontSize: '10px', color: '#C2410C', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>{p.category}</p>
                                            <h3 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '14px', fontWeight: 400, color: '#1C1917', marginBottom: '6px' }}>{p.name}</h3>
                                            <span style={{ fontSize: '15px', fontWeight: 600, color: '#1C1917' }}>{`\u20B9${p.price.toLocaleString()}`}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default ProductDetail13;
